import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { CheckCircle2, Send } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1400&q=80";

const provinces = [
  "British Columbia", "Alberta", "Saskatchewan", "Manitoba", "Ontario",
  "Quebec", "New Brunswick", "Nova Scotia", "PEI", "Newfoundland & Labrador",
  "Yukon", "Northwest Territories", "Nunavut", "Remote (Canada-wide)",
];

const specializations = [
  "AI / ML & Data", "Cloud & Platform", "Cybersecurity & GRC", "Quantum",
  "DevSecOps", "Digital Transformation",
  "Finance & Accounting", "HR & People Ops", "Legal & Compliance",
  "Healthcare & Life Sciences", "Sales & Marketing",
  "Travel Nursing (RN/NP/LPN)", "Allied Health (PT/OT/RT/MLT/MRT)", "Non-Clinical Healthcare",
];

export default function ForCandidates() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", current_role: "",
    years_experience: 5, specialization: "AI / ML & Data",
    preferred_province: "British Columbia", resume_link: "", linkedin: "", summary: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, isNum = false) => (e) =>
    setForm((s) => ({ ...s, [k]: isNum ? Number(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/candidate-submission", form);
      toast.success("Profile received. We'll reach out with relevant opportunities.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="for-candidates-page">
      <PageHero
        eyebrow="For Candidates"
        title={<>Work with the firm that <span className="italic" style={{ color: "var(--accent-mint)" }}>actually reads your résumé.</span></>}
        subtitle="We represent senior-level candidates across Canada's highest-demand tech and general staffing practices. No spam lists. No mass InMails. Just curated roles that match what you're actually looking for."
        image={HERO_IMG}
        imageAlt="Professional at workstation"
        testId="candidates-hero"
      />

      <section className="py-16 md:py-24" data-testid="candidate-form-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-4">
            <div className="eyebrow mb-3">How we work with talent</div>
            <h2 className="display-md mb-6">Discreet. Curated. Canadian.</h2>
            <ul className="space-y-4 text-[color:var(--muted)]">
              <li className="flex gap-3">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent-mint)" }} />
                <span>We never share your profile without your explicit go-ahead.</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent-mint)" }} />
                <span>Every role you see has a real budget and a real decision-maker lined up.</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent-mint)" }} />
                <span>Compensation and expectations are transparent from the first call.</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent-mint)" }} />
                <span>Full PIPEDA-compliant data handling. Your data is yours.</span>
              </li>
            </ul>
          </aside>

          <div className="md:col-span-8">
            {submitted ? (
              <div className="card text-center py-16" data-testid="candidate-submitted-card">
                <CheckCircle2 size={56} className="mx-auto mb-4" style={{ color: "var(--accent-mint)" }} />
                <h3 className="display-md mb-3">Profile received.</h3>
                <p className="text-[color:var(--muted)] max-w-md mx-auto">
                  Our talent team will review your profile and reach out when we have a role that matches.
                  Typical response time is 3–5 business days.
                </p>
              </div>
            ) : (
              <form className="card space-y-5" onSubmit={submit} data-testid="candidate-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label">Full name *</label>
                    <input required className="field-input" value={form.full_name} onChange={update("full_name")} data-testid="candidate-field-full_name" />
                  </div>
                  <div>
                    <label className="field-label">Email *</label>
                    <input required type="email" className="field-input" value={form.email} onChange={update("email")} data-testid="candidate-field-email" />
                  </div>
                  <div>
                    <label className="field-label">Phone (optional)</label>
                    <input className="field-input" value={form.phone} onChange={update("phone")} data-testid="candidate-field-phone" />
                  </div>
                  <div>
                    <label className="field-label">Current role *</label>
                    <input required className="field-input" placeholder="e.g. Senior ML Engineer" value={form.current_role} onChange={update("current_role")} data-testid="candidate-field-current_role" />
                  </div>
                  <div>
                    <label className="field-label">Years of experience *</label>
                    <input required type="number" min="0" max="60" className="field-input" value={form.years_experience} onChange={update("years_experience", true)} data-testid="candidate-field-years_experience" />
                  </div>
                  <div>
                    <label className="field-label">Specialization</label>
                    <select className="field-select" value={form.specialization} onChange={update("specialization")} data-testid="candidate-field-specialization">
                      {specializations.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Preferred province</label>
                    <select className="field-select" value={form.preferred_province} onChange={update("preferred_province")} data-testid="candidate-field-preferred_province">
                      {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Résumé link (optional)</label>
                    <input type="url" className="field-input" placeholder="Google Drive / Dropbox link" value={form.resume_link} onChange={update("resume_link")} data-testid="candidate-field-resume_link" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label">LinkedIn URL (optional)</label>
                    <input type="url" className="field-input" placeholder="https://linkedin.com/in/…" value={form.linkedin} onChange={update("linkedin")} data-testid="candidate-field-linkedin" />
                  </div>
                </div>

                <div>
                  <label className="field-label">Short summary (optional)</label>
                  <textarea rows={4} className="field-textarea"
                    placeholder="What kind of role are you looking for? Remote vs. hybrid? Comp range?"
                    value={form.summary} onChange={update("summary")}
                    data-testid="candidate-field-summary" />
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <button type="submit" disabled={submitting} className="btn-primary" data-testid="candidate-submit-btn">
                    {submitting ? "Sending…" : "Submit profile"} <Send size={16} />
                  </button>
                  <p className="text-xs text-[color:var(--muted)]">
                    PIPEDA-compliant. We never share your profile without consent.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
